import { type Dispatch, type FC, type SetStateAction, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Download, Search } from "lucide-react";
import {
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableTable,
  DashboardDataTableSortButton,
  TablePagePagination,
  DefinitionTooltip,
  dashboardDataTable as dt,
  type DefinitionTooltipInfo,
} from "src/components/common";
import {
  EMPTY_API_VALUE,
  formatOptionalNumber,
  getMetaColumnTooltip,
  getRoasToneClass,
  mapAdsetApiItem,
  type CampaignSortKey,
  type CampaignSummary,
} from "./MetaTableConfig";
import { formatPaidMediaCount, formatPaidMediaDateText, formatPaidMediaPercent, formatPaidMediaRoas } from "../../utils/PaidMediaNumberFormat";

interface MetaCampaignsTableProps {
  metaSectionHelp: (_key: string) => DefinitionTooltipInfo;
  searchCampaigns: string;
  setSearchCampaigns: Dispatch<SetStateAction<string>>;
  setCurrentCampaignPage: Dispatch<SetStateAction<number>>;
  isExporting: boolean;
  exportCampaignsCsv: () => Promise<void>;
  toggleCampaignSort: (_key: CampaignSortKey) => void;
  paginatedCampaigns: CampaignSummary[];
  expandedCampaigns: Set<string>;
  toggleCampaignExpanded: (_id: string) => void;
  formatMoney: (
    _value: number,
    _options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ) => string;
  formatMoneyShort: (_value: number) => string;
  currentCampaignPage: number;
  campaignTotalPages: number;
}

const MetaCampaignsTable: FC<MetaCampaignsTableProps> = ({
  metaSectionHelp,
  searchCampaigns,
  setSearchCampaigns,
  setCurrentCampaignPage,
  isExporting,
  exportCampaignsCsv,
  toggleCampaignSort,
  paginatedCampaigns,
  expandedCampaigns,
  toggleCampaignExpanded,
  formatMoney,
  formatMoneyShort,
  currentCampaignPage,
  campaignTotalPages,
}) => {
  const { t } = useTranslation();

  return (
              <DashboardDataTablePanel className="paid-media-table-panel--attached paid-media-campaign-table-panel">
                <DashboardDataTablePanelHeader>
                  <div className="paid-media-panel__heading">
                    <h3>{t("metaAdsReport.sections.campaignPerformance")}</h3>
                    <DefinitionTooltip
                      label={t("metaAdsReport.sections.campaignPerformance")}
                      info={metaSectionHelp("campaignPerformance")}
                    />
                  </div>
                  <div
                    className="paid-media-table-toolbar"
                    role="toolbar"
                    aria-label={t("metaAdsReport.toolbars.campaignsSearch")}
                  >
                    <label className={`${dt.search} paid-media-table-toolbar__search`}>
                      <Search size={16} />
                      <input
                        type="search"
                        value={searchCampaigns}
                        placeholder={t("metaAdsReport.placeholders.searchCampaigns")}
                        onChange={event => {
                          setSearchCampaigns(event.target.value);
                          setCurrentCampaignPage(1);
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      className="paid-media-export-btn"
                      disabled={isExporting}
                      onClick={() => void exportCampaignsCsv()}
                    >
                      <Download size={16} aria-hidden />
                      {isExporting
                        ? t("metaAdsReport.export.exporting")
                        : t("metaAdsReport.export.csv")}
                    </button>
                  </div>
                </DashboardDataTablePanelHeader>
                <DashboardDataTableScroll>
                  <DashboardDataTableTable className="paid-media-campaign-performance-table">
                    <thead>
                      <tr>
                        <th>
                          <DashboardDataTableSortButton
                            onClick={() => toggleCampaignSort("campaignName")}
                          >
                            {t("metaAdsReport.campaignTable.campaign")}
                          </DashboardDataTableSortButton>
                        </th>
                        {(
                          [
                            "budgetCategory",
                            "campaignStartAt",
                            "campaignStopAt",
                            "campaignCreatedAt",
                            "dailyBudget",
                            "linkClicks",
                          ] as const
                        ).map(column => (
                          <th key={column} className={dt.number}>
                            <span className={dt.thHelpRow}>
                              {t(`metaAdsReport.campaignTable.${column}`)}
                              <DefinitionTooltip
                                label={t(`metaAdsReport.campaignTable.${column}`)}
                                info={getMetaColumnTooltip(t, column)}
                                formulaTitle={t("webAnalytics.kpi.formula")}
                              />
                            </span>
                          </th>
                        ))}
                        {(
                          [
                            ["revenue", "revenue", dt.number],
                            ["spend", "spend", dt.number],
                            ["impressions", "impressions", dt.thCenter],
                            ["ctr", "ctr", dt.thCenter],
                            ["cpc", "cpc", dt.thCenter],
                            ["cpa", "cpa", dt.thCenter],
                            ["roas", "roas", dt.thCenter],
                            ["convShort", "conversions", dt.thCenter],
                          ] as const
                        ).map(([column, sortKey, alignClass], columnIndex, columns) => (
                          <th key={column} className={alignClass}>
                            <span className={dt.thHelpRow}>
                              <DashboardDataTableSortButton
                                onClick={() => toggleCampaignSort(sortKey)}
                              >
                                {t(`metaAdsReport.campaignTable.${column}`)}
                              </DashboardDataTableSortButton>
                              <DefinitionTooltip
                                label={t(`metaAdsReport.campaignTable.${column}`)}
                                info={getMetaColumnTooltip(t, column)}
                                align={
                                  columnIndex >= columns.length - 3 ? "end" : "start"
                                }
                                formulaTitle={t("webAnalytics.kpi.formula")}
                              />
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCampaigns.map(row => {
                        const isExpanded = expandedCampaigns.has(row.id);
                        const hasAdsets = row.adsets.length > 0;

                        return (
                          <Fragment key={row.id}>
                            <tr>
                              <td>
                                <div className="paid-media-campaign-cell">
                                  {hasAdsets ? (
                                    <button
                                      type="button"
                                      className="paid-media-campaign-expand"
                                      aria-expanded={isExpanded}
                                      aria-label={t("metaAdsReport.campaignTable.toggleAdSets")}
                                      onClick={() => toggleCampaignExpanded(row.id)}
                                    >
                                      {isExpanded ? (
                                        <ChevronDown size={16} />
                                      ) : (
                                        <ChevronRight size={16} />
                                      )}
                                    </button>
                                  ) : (
                                    <span className="paid-media-campaign-expand paid-media-campaign-expand--placeholder" />
                                  )}
                                  <div>
                                    <strong>{row.campaignName}</strong>
                                    {hasAdsets ? (
                                      <span className="paid-media-mini-tag paid-media-mini-tag--muted">
                                        {t("metaAdsReport.campaignTable.adSetCount", {
                                          count: row.adsets.length,
                                        })}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              </td>
                              <td className={dt.number}>
                                {row.budgetCategory ?? EMPTY_API_VALUE}
                              </td>
                              <td className={dt.number}>
                                {formatPaidMediaDateText(row.campaignStartAt) || EMPTY_API_VALUE}
                              </td>
                              <td className={dt.number}>
                                {formatPaidMediaDateText(row.campaignStopAt) || EMPTY_API_VALUE}
                              </td>
                              <td className={dt.number}>
                                {formatPaidMediaDateText(row.campaignCreatedAt) || EMPTY_API_VALUE}
                              </td>
                              <td className={dt.number}>
                                {formatOptionalNumber(row.dailyBudget, formatMoney)}
                              </td>
                              <td className={dt.number}>
                                {formatOptionalNumber(row.linkClicks)}
                              </td>
                              <td className={dt.number}>
                                {formatOptionalNumber(row.revenue, formatMoney)}
                              </td>
                              <td className={dt.number}>
                                {formatOptionalNumber(row.spend, formatMoney)}
                              </td>
                              <td className={dt.tdCenter}>
                                {formatOptionalNumber(row.impressions)}
                              </td>
                              <td className={dt.tdCenter}>
                                {formatOptionalNumber(row.ctr, formatPaidMediaPercent)}
                              </td>
                              <td className={dt.tdCenter}>
                                {formatOptionalNumber(row.cpc, formatMoneyShort)}
                              </td>
                              <td className={dt.tdCenter}>
                                {formatOptionalNumber(row.cpa, formatMoneyShort)}
                              </td>
                              <td className={`${dt.tdCenter} ${getRoasToneClass(row.roas ?? 0)}`}>
                                {formatOptionalNumber(row.roas, formatPaidMediaRoas)}
                              </td>
                              <td className={dt.tdCenter}>
                                {formatOptionalNumber(row.conversions)}
                              </td>
                            </tr>
                            {isExpanded
                              ? row.adsets.map((adset, adsetIndex) => {
                                  const adsetRow = mapAdsetApiItem(adset, adsetIndex);
                                  return (
                                    <tr
                                      key={`${row.id}-${adsetRow.id}`}
                                      className="paid-media-table-subrow"
                                    >
                                      <td>
                                        <span className="paid-media-table-subrow__label">
                                          <span className="paid-media-table-subrow__eyebrow">
                                            {t("metaAdsReport.campaignTable.adSetRowLabel", {
                                              index: formatPaidMediaCount(adsetIndex + 1),
                                            })}
                                          </span>
                                          <span>{adsetRow.name}</span>
                                        </span>
                                      </td>
                                      <td className={dt.number}>
                                        {adsetRow.budgetCategory ?? EMPTY_API_VALUE}
                                      </td>
                                      <td className={dt.number}>
                                        {formatPaidMediaDateText(adsetRow.adsetStartAt) || EMPTY_API_VALUE}
                                      </td>
                                      <td className={dt.number}>
                                        {formatPaidMediaDateText(adsetRow.adsetEndAt) || EMPTY_API_VALUE}
                                      </td>
                                      <td className={dt.number}>
                                        {formatPaidMediaDateText(adsetRow.adsetCreatedAt) || EMPTY_API_VALUE}
                                      </td>
                                      <td className={dt.number}>
                                        {formatOptionalNumber(adsetRow.dailyBudget, formatMoney)}
                                      </td>
                                      <td className={dt.number}>
                                        {formatOptionalNumber(adsetRow.linkClicks)}
                                      </td>
                                      <td className={dt.number}>
                                        {formatOptionalNumber(adsetRow.revenue, formatMoney)}
                                      </td>
                                      <td className={dt.number}>
                                        {formatMoney(adsetRow.spend)}
                                      </td>
                                      <td className={dt.tdCenter}>
                                        {formatPaidMediaCount(adsetRow.impressions)}
                                      </td>
                                      <td className={dt.tdCenter}>
                                        {formatOptionalNumber(adsetRow.ctr, formatPaidMediaPercent)}
                                      </td>
                                      <td className={dt.tdCenter}>
                                        {formatOptionalNumber(adsetRow.cpc, formatMoneyShort)}
                                      </td>
                                      <td className={dt.tdCenter}>
                                        {formatOptionalNumber(adsetRow.cpa, formatMoneyShort)}
                                      </td>
                                      <td
                                        className={`${dt.tdCenter} ${getRoasToneClass(adsetRow.roas)}`}
                                      >
                                        {formatPaidMediaRoas(adsetRow.roas)}
                                      </td>
                                      <td className={dt.tdCenter}>
                                        {formatPaidMediaCount(adsetRow.conversions)}
                                      </td>
                                    </tr>
                                  );
                                })
                              : null}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </DashboardDataTableTable>
                </DashboardDataTableScroll>
                <TablePagePagination
                  currentPage={currentCampaignPage}
                  totalPages={campaignTotalPages}
                  onPrevious={() =>
                    setCurrentCampaignPage(page => Math.max(1, page - 1))
                  }
                  onNext={() =>
                    setCurrentCampaignPage(page =>
                      Math.min(campaignTotalPages, page + 1)
                    )
                  }
                />
              </DashboardDataTablePanel>
  );
};

export default MetaCampaignsTable;
